import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFormType } from '@neo/blocks/forms/types'
import type { SelectOptionWithTab } from '@neo/blocks/select-with-tabs'
import { Button } from '@neo/index'

import { Slider } from '@neo/primitives/slider'
import { Tooltip } from '@neo/primitives/tooltip'
import { usePlayAudio } from '@shared/hooks/use-play-audio'
import { Info, PlusIcon, Trash2 } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RProviders, RVoice } from 'v2/pages/flow/sidebar/types'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import z from 'zod'
import { VoicePlayControl } from '../../components/voice-play-control'
import {
  ElevenLabsModels,
  ProviderTypes,
  textToSpeechSchema,
} from '../../config/agent-settings.validation.config'
import type { AgentSettingsFormValues } from '../../config/base.config'
import { invalidSelectValueSchema } from '../../config/validation.config'
import { useTextToSpeechMutation, useVoicesQuery } from '../../queries'
import type { VoicePreviewParams } from '../../types'
import { createVoicePreviewHandler } from '../../utils'
import {
  clampVoiceSpeed,
  filterGatedVoices,
  findTtsInfoByVoiceId,
  getTtsModelForVoice,
  getVoiceAiConfig,
  getVoiceProvider,
  getVoiceSpeedBounds,
  resolveTtsModelForVoice,
} from '../../utils/lang-utils'
import { VoiceForm } from '../ai-agents/voice-form'
import { BackgroundAudioConfig } from './agent-settings.background-audio'
import { CustomVoiceModal } from './custom-voice'

interface TTSConfigProps {
  form: GenericFormType<AgentSettingsFormValues>
  voices: RVoice[]
  providers: RProviders | undefined
  region: string
}

// Helper function to handle voice field changes
const handleVoiceFieldChange = (
  formState: AgentSettingsFormValues | undefined,
  form: GenericFormType<AgentSettingsFormValues> | undefined,
  region: string,
  providers: RProviders | undefined,
  voices: RVoice[] | undefined,
) => {
  if (!formState || !form) return

  const languageValue = formState.voice_ai_config?.language || ''
  const selectedLanguagesArray = languageValue.split(',').filter(Boolean)
  const sttTtsConfig = getVoiceAiConfig(selectedLanguagesArray, region, providers)
  const voiceId = formState.voice_ai_config?.text_to_speech?.voice

  if (!voiceId) return

  // Resolve provider/label from the selected voice
  const ttsInfo = findTtsInfoByVoiceId(providers, voiceId)
  const provider = getVoiceProvider(providers, voices, voiceId)
  if (ttsInfo) {
    form.setFieldValue('voice_ai_config.text_to_speech.voice_label', ttsInfo.voiceLabel)
    form.setFieldValue('voice_ai_config.text_to_speech.provider', ttsInfo.providerId)
  } else {
    const voiceRow = voices?.find((v) => v.id === voiceId)
    if (voiceRow) {
      form.setFieldValue('voice_ai_config.text_to_speech.voice_label', voiceRow.label)
    }
    // Fallback to default provider from language config
    form.setFieldValue(
      'voice_ai_config.text_to_speech.provider',
      provider || sttTtsConfig.ttsProvider,
    )
  }

  // Keep the model in lockstep with the voice's provider on every voice switch: a
  // non-ElevenLabs voice (Sarvam) forces its provider model, an ElevenLabs voice keeps
  // its radio-driven model. This closes the gap where a Sarvam voice kept a stale
  // ElevenLabs model (which 403s at Sarvam).
  form.setFieldValue(
    'voice_ai_config.text_to_speech.model',
    resolveTtsModelForVoice(
      providers,
      voices,
      voiceId,
      formState.voice_ai_config?.text_to_speech?.model,
    ),
  )
  repairModelFieldMeta(form)
}

// The Voice Model radio is unmounted for non-ElevenLabs voices, and TanStack 0.28
// setFieldValue on a never-mounted field creates meta WITHOUT errorMap — the field
// then crashes its first validation when a later ElevenLabs voice mounts it. Repair
// after every model write; a no-op when the field has real meta.
const repairModelFieldMeta = (form: GenericFormType<AgentSettingsFormValues>) => {
  form.setFieldMeta('voice_ai_config.text_to_speech.model', (prev) => ({
    ...prev,
    errorMap: prev?.errorMap ?? {},
    errors: prev?.errors ?? [],
  }))
}

// Slider to override the selected voice's speed at the flow level. Defaults to the
// voice's DB speed (meta.voice_settings.speed) and only persists once the user drags it.
const VoiceSpeedSlider = ({
  form,
  voices,
  providers,
}: {
  form: GenericFormType<AgentSettingsFormValues>
  voices: RVoice[] | undefined
  providers: RProviders | undefined
}) => {
  const voiceId = form.useStore((state) => state.values.voice_ai_config?.text_to_speech?.voice)
  const voiceSpeed = form.useStore(
    (state) => state.values.voice_ai_config?.text_to_speech?.voice_speed,
  )

  // '' while providers/voices are still loading (or the voice is unknown) —
  // bounds then fall back to the ElevenLabs range for DISPLAY, but nothing may
  // be written back on that guess (see the clamp effect below).
  const voiceProvider = useMemo(
    () => getVoiceProvider(providers, voices, voiceId),
    [providers, voices, voiceId],
  )
  const bounds = useMemo(() => getVoiceSpeedBounds(voiceProvider), [voiceProvider])

  // The selected voice's DB speed (falls back to 1.0 when not stored on the voice)
  const dbSpeed = useMemo(() => {
    const selectedVoice = voices?.find((v) => v.id === voiceId)
    return selectedVoice?.meta?.voice_settings?.speed ?? 1
  }, [voices, voiceId])

  // When the user switches to a different voice, clear the flow-level override so the
  // slider re-defaults to the new voice's DB speed (undefined => falls back to dbSpeed).
  // Guard against initial hydration (empty -> real voice id): only reset on a genuine
  // voice switch between two real voices, otherwise we'd wipe the saved speed on mount.
  const prevVoiceIdRef = useRef(voiceId)
  useEffect(() => {
    if (prevVoiceIdRef.current !== voiceId) {
      const hadPreviousVoice = !!prevVoiceIdRef.current
      prevVoiceIdRef.current = voiceId
      if (hadPreviousVoice) {
        form.setFieldValue('voice_ai_config.text_to_speech.voice_speed', undefined)
      }
    }
  }, [voiceId, form])

  // A provider switch narrows/widens the valid range; clamp any persisted override
  // into the new bounds so a saved out-of-range value cannot be re-saved. Only
  // once the provider is actually known: while providers/voices are loading the
  // bounds are a display fallback, and clamping a saved Sarvam 2.0 against the
  // ElevenLabs range would silently rewrite it to 1.2 and dirty the form.
  useEffect(() => {
    if (voiceSpeed === undefined || !voiceProvider) return
    const clamped = clampVoiceSpeed(voiceSpeed, bounds)
    if (clamped !== voiceSpeed) {
      form.setFieldValue('voice_ai_config.text_to_speech.voice_speed', clamped)
    }
  }, [voiceSpeed, bounds, voiceProvider, form])

  if (!voiceId) return null

  const value = clampVoiceSpeed(voiceSpeed ?? dbSpeed, bounds)

  const handleSpeedChange = (val: number[]) => {
    form.setFieldValue('voice_ai_config.text_to_speech.voice_speed', val[0])
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1 text-p-500 text-primary">
        Voice speed
        <Tooltip content="Adjust how fast the agent speaks. 1.0 is the voice's normal speed.">
          <Info className="size-3 text-tertiary" />
        </Tooltip>
      </div>
      <div className="flex items-center gap-3">
        <Slider
          value={[value]}
          onValueChange={handleSpeedChange}
          min={bounds.min}
          max={bounds.max}
          step={bounds.step}
          data-testid="slider-voice-speed"
        />
        <span className="text-p-500 font-jetbrains tabular-nums text-primary w-10 text-right">
          {value.toFixed(2)}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-s-400 text-secondary">Slower</span>
        <span className="text-s-400 text-secondary">Faster</span>
      </div>
    </div>
  )
}

export const TTSConfig = React.memo(({ form, voices, providers, region }: TTSConfigProps) => {
  const { isPlaying, play, stop } = usePlayAudio()
  const textToSpeechMutation = useTextToSpeechMutation()
  const [loadingVoiceId, setLoadingVoiceId] = useState<string | null>(null)
  const [showCustomVoiceModal, setShowCustomVoiceModal] = useState(false)
  const { AgentRunnerNimbleEnabled } = useFeatureFlags()

  // Get current language value from form state
  const languageValue = form.useStore((state) => state.values.voice_ai_config?.language || '')

  // The flow's already-saved voice (the persisted snapshot, not the in-edit value). Sentinel
  // returns it regardless of the language filter so an existing flow never loses its saved
  // voice — e.g. an ElevenLabs voice on a flow whose language is now Sarvam-only.
  const savedVoiceId = useMemo(
    () => form.options.defaultValues?.voice_ai_config?.text_to_speech?.voice,
    [form],
  )

  // Call hook at component level
  const { data: voicesData, isLoading: isLoadingVoices } = useVoicesQuery(
    languageValue,
    savedVoiceId,
  )

  // Handle language change - clear voice if not available in new language
  const handleLanguageChange = useCallback(
    (
      formState: AgentSettingsFormValues | undefined,
      form: GenericFormType<AgentSettingsFormValues> | undefined,
    ) => {
      if (!formState || !form) return

      const currentVoiceId = formState.voice_ai_config?.text_to_speech?.voice

      // If a voice is selected, check if it exists in the new voices list
      if (currentVoiceId && voicesData) {
        const voiceExists = voicesData.some((voice) => voice.id === currentVoiceId)

        // If voice doesn't exist in new language's voices, clear it
        if (!voiceExists) {
          form.setFieldValue('voice_ai_config.text_to_speech.voice', '')
          form.setFieldValue('voice_ai_config.text_to_speech.voice_label', '')
          form.setFieldValue('voice_ai_config.text_to_speech.provider', '')
          form.setFieldValue('voice_ai_config.text_to_speech.model', '')
        } else {
          // Voice exists, just update the label
          handleVoiceFieldChange(formState, form, region, providers, voicesData)
        }
      }
    },
    [voicesData, region, providers],
  )

  const handlePlayPreview = useMemo(
    () => async (params: VoicePreviewParams) => {
      try {
        setLoadingVoiceId(params.voiceId)
        await createVoicePreviewHandler(play, textToSpeechMutation)(params)
      } finally {
        setLoadingVoiceId(null)
      }
    },
    [play, textToSpeechMutation],
  )

  const audioHandlers = useMemo(
    () => ({ isPlaying, handlePlayPreview, loadingVoiceId, stop }),
    [isPlaying, handlePlayPreview, loadingVoiceId, stop],
  )

  const selectedVoiceId = form.useStore(
    (state) => state.values.voice_ai_config?.text_to_speech?.voice,
  )
  const selectedModel = form.useStore(
    (state) => state.values.voice_ai_config?.text_to_speech?.model,
  )

  // The Voice Model radio group is ElevenLabs specific. Any other provider has no
  // radio and derives its model from the selected voice instead. An empty provider
  // (no voice selected yet) keeps today's ElevenLabs default.
  const voiceProvider = useMemo(
    () => getVoiceProvider(providers, voicesData, selectedVoiceId),
    [providers, voicesData, selectedVoiceId],
  )
  const isElevenLabsVoice = !voiceProvider || voiceProvider === ProviderTypes.ElevenLabs

  const resolvedTtsModel = useMemo(
    () => getTtsModelForVoice(providers, voicesData, selectedVoiceId),
    [providers, voicesData, selectedVoiceId],
  )

  // Keep the persisted model in sync with a non-ElevenLabs voice (covers a saved voice
  // whose model predates the provider, and voices resolvable only from voicesData).
  useEffect(() => {
    if (isLoadingVoices || isElevenLabsVoice || !resolvedTtsModel) return
    if (selectedModel === resolvedTtsModel) return
    form.setFieldValue('voice_ai_config.text_to_speech.model', resolvedTtsModel)
    repairModelFieldMeta(form)
  }, [isLoadingVoices, isElevenLabsVoice, resolvedTtsModel, selectedModel, form])

  return (
    <div className="grid gap-5">
      {isElevenLabsVoice && (
        <FieldsRenderer<AgentSettingsFormValues>
          form={form}
          baseName="voice_ai_config.text_to_speech"
          fields={[
            {
              name: 'voice_ai_config.text_to_speech.model',
              componentType: 'radioGroup',
              label: 'Voice Model',
              className: 'col-span-full',
              zodValidation: textToSpeechSchema.shape.model,
              inputProps: {
                options: [
                  {
                    label: 'Higher speech accuracy, higher latency',
                    value: ElevenLabsModels.TurboV2_5,
                  },
                  {
                    label: 'High speech accuracy, lower latency',
                    value: ElevenLabsModels.FlashV2_5,
                  },
                ],
                optionsClassName: 'grid grid-cols-1 gap-2 md:grid-cols-2',
                optionClassName:
                  'w-full rounded-md border border-border bg-background p-3 transition-colors hover:bg-hv1 [&.checked]:border-brand [&.checked]:bg-hv1',
                labelClassName: 'text-s-400 leading-5 text-foreground',
              },
            },
          ]}
        />
      )}
      <FieldsRenderer<AgentSettingsFormValues>
        form={form}
        baseName="voice_ai_config.text_to_speech"
        fields={[
          {
            name: 'voice_ai_config.text_to_speech.voice',
            componentType: 'selectWithTabs',
            label: 'Voice',
            zodValidation: invalidSelectValueSchema(
              textToSpeechSchema.shape.voice,
              voices?.map((voice) => voice.id) ?? [],
            ),
            className: 'col-span-full relative',
            onChangeListenTo: ['voice_ai_config.language'],
            afterOnChange: (formState, _index, form) => {
              handleLanguageChange(formState, form)
            },
            getDynamicProps: (_value) => {
              // Use voicesData from component level hook
              const voiceFromProvider = filterGatedVoices(
                voicesData,
                AgentRunnerNimbleEnabled,
                savedVoiceId,
              )?.sort((a, b) => a.label.localeCompare(b.label))

              return {
                zodVal: textToSpeechSchema.shape.voice.superRefine((val, ctx) => {
                  if (isLoadingVoices) {
                    return
                  }

                  if (!val) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: 'Voice is required',
                    })
                    return
                  }
                  // Check if voice exists in filtered voices OR in all provider voices (for backward compatibility)
                  const voiceExistsInFiltered = voiceFromProvider?.some((voice) => voice.id === val)

                  if (!voiceExistsInFiltered) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: 'Selected value not found in the list of available options',
                    })
                  }
                }),
                inputProps: {
                  placeholder: 'Select voice',
                  inputPlaceholder: 'Search voices',
                  size: 'lg',
                  selectConfig: {
                    tabs: [
                      { label: 'Male', value: 'male' },
                      { label: 'Female', value: 'female' },
                      { label: 'Custom', value: 'custom' },
                    ],
                    options:
                      voiceFromProvider?.map((voice) => ({
                        label: (
                          <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-0.5">
                            <div className="truncate text-p-500 text-foreground">{voice.label}</div>
                            <div className="line-clamp-2 text-s-400 text-secondary">
                              {voice.description}
                            </div>
                          </div>
                        ),
                        searchValue: voice.label,
                        value: voice.id,
                        tab: voice.meta?.type ? 'custom' : voice.meta?.gender?.toLowerCase(),
                        icon: (
                          <VoicePlayControl
                            voice={voice}
                            isPlaying={audioHandlers.isPlaying}
                            loadingVoiceId={audioHandlers.loadingVoiceId}
                            onPlayPreview={(voiceId, previewUrl, model, provider, languageCode) =>
                              audioHandlers.handlePlayPreview({
                                voiceId,
                                previewUrl,
                                model,
                                provider,
                                languageCode,
                              })
                            }
                            onStop={audioHandlers.stop}
                          />
                        ),
                      })) || [],
                  },
                  searchable: true,
                  clearable: true,
                  headerAction: (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-brand-text hover:bg-hv1 hover:text-brand-text"
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowCustomVoiceModal(true)
                      }}
                      data-testid="button-add-custom-voice"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add custom voice
                    </Button>
                  ),
                  selectedValueRenderer: (props: {
                    value: string
                    options: SelectOptionWithTab[]
                  }) => {
                    // A saved voice may sit outside the current language's list (language
                    // switched, or a cross-provider voice). Resolve through the language
                    // list, then the provider's full voice list, and finally fall back to
                    // the persisted label so the trigger still shows the saved voice.
                    let voice: RVoice | { id: string; label: string } | undefined =
                      voiceFromProvider?.find((v) => v.id === props.value) ??
                      voicesData?.find((v) => v.id === props.value) ??
                      voices?.find((v) => v.id === props.value)

                    if (!voice && props.value) {
                      const savedLabel =
                        form.getFieldValue('voice_ai_config.text_to_speech.voice_label') ||
                        props.value
                      voice = { id: props.value, label: savedLabel }
                    }

                    return (
                      <VoicePlayControl
                        voice={voice}
                        isPlaying={audioHandlers.isPlaying}
                        loadingVoiceId={audioHandlers.loadingVoiceId}
                        onPlayPreview={(voiceId, previewUrl, model, provider, languageCode) =>
                          audioHandlers.handlePlayPreview({
                            voiceId,
                            previewUrl,
                            model,
                            provider,
                            languageCode,
                          })
                        }
                        onStop={audioHandlers.stop}
                        options={{ showLabel: true, className: 'size-7' }}
                      />
                    )
                  },
                },
              }
            },
          },
        ]}
      />
      <VoiceSpeedSlider form={form} voices={voicesData} providers={providers} />
      <BackgroundAudioConfig form={form} />
      <FieldsRenderer<AgentSettingsFormValues>
        form={form}
        baseName="voice_ai_config.text_to_speech"
        fields={[
          {
            name: 'voice_ai_config.text_to_speech.pronunciation',
            type: 'array',
            defaultNewValue: { word: '', guide: '' },
            className: 'relative',
            removeItemButton: (
              <Tooltip content="Remove pronunciation">
                <Button variant="ghostDestructive" className="absolute right-2 top-2" type="button">
                  <Trash2 size={16} />
                </Button>
              </Tooltip>
            ),
            addItemButton: (props) => (
              <div>
                <Button
                  className="mt-2 flex items-center text-brand-text hover:bg-hv1 hover:text-brand-text"
                  variant="ghost"
                  type="button"
                  {...props}
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add pronunciation
                </Button>
              </div>
            ),
            subFields: [
              {
                name: '',
                type: 'sub-item',
                onChangeListenTo: [
                  'voice_ai_config.text_to_speech.pronunciation',
                  'voice_ai_config.text_to_speech.voice',
                ],
                component: (props) => (
                  <VoiceForm
                    {...(props as {
                      value: { word: string; guide: string }
                      onChange: (value: { word: string; guide: string }) => void
                      arrayIndex: number
                      form: GenericFormType<AgentSettingsFormValues>
                    })}
                  />
                ),
                zodValidation: z.any(),
                eventDataExtractor: (data) => data as Record<string, unknown>,
              },
            ],
          },
        ]}
      />
      <CustomVoiceModal open={showCustomVoiceModal} onOpenChange={setShowCustomVoiceModal} />
    </div>
  )
})
